import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { DetailList } from '@tecuity/granite-ui/DetailList'
import IconSearch from '@tecuity/sos-icons/react/search'
import IconPencil from '@tecuity/sos-icons/react/pencil'
import PrintIcon from '@tecuity/sos-icons/react/print-solid'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import ActionButton from '../ActionButton/ActionButton'
import {
  getInitialBusinessEntityRecordData,
  getInitialTrademarkRecordData,
  getFacsimileSignatureDetails,
  getEditRecordUrl,
} from '../RecordDetailsPage/RecordDetailsActions'
import { Spinner } from 'components/Loaders/Loaders'
import { ADD_PDF_TO_LIST, pdfTypes } from 'components/ReportViewer/ReportViewerReducer'
import { setErrorToast } from '../../actions'
import { openSpecificImage } from '../ImageViewer/ImageViewerActions'
import { checkDLNValid } from './BOSearchActions'
import moment from 'moment'
import VoidModal from 'components/VoidModal/VoidModal'
import { VOID_FILING } from '../../requests.js'
import { CorrespondenceDownload } from '../RecordHistory/RecordHistory'
import * as api from '../../requests.js'
import { SET_RECORD_DETAILS } from '../RecordDetailsPage/RecordDetailsReducer.js'
import SearchRecordHistory from './SearchRecordHistory.js'

const VIEW_HISTORY = process.env.VIEW_HISTORY

const SearchItemInfo = ({
  selectedItem,
  columns,
  searchFields,
  getRedirect,
  isMiscSearch,
  miscSearchTemplate,
  sourceTypeParam,
  moduleId,
}) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { searchResults } = useSelector((state) => state.boSearch, shallowEqual)
  const { recordDetails } = useSelector((state) => state.record, shallowEqual)
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const [loading, setLoading] = useState(false)
  const [voidModalOpen, setVoidModalOpen] = useState(false)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)

  const getRecordDetails = (name) => {
    switch (name) {
      case 'BusinessEntitySearch':
        if (selectedItem['File #']) {
          dispatch(getInitialBusinessEntityRecordData(selectedItem['File #']))
        }
        break
      case 'TrademarkSearch':
        if (selectedItem['Trademark #'] || selectedItem['Record Number']) {
          dispatch(
            getInitialTrademarkRecordData(selectedItem['Trademark #'] || selectedItem['Record Number'])
          )
        }
        break
      case 'FacsimileSignatureSearch':
        if (selectedItem['Doc #']) {
          setLoading(true)
          dispatch(getFacsimileSignatureDetails(selectedItem['Doc #']))
        }
        break
      case 'OrderSearch':
        if (selectedItem['Certification Filing #']) {
          setLoading(true)
          api.GET_ORDER_FILING_DETAILS(
            selectedItem['Certification Filing #'],
            (record) => {
              dispatch({ type: SET_RECORD_DETAILS, record: record })
              setLoading(false)
            },
            (err) => {
              console.error(err)
              setLoading(false)
            }
          )
        }
        break
      case 'RegisteredAgentEntitySearch':
        if (selectedItem['File #']) {
          setLoading(true)
          dispatch(getEditRecordUrl(name, selectedItem['File #'], selectedItem))
        }
        break
      case 'ServiceOfProcessSearch':
        if (selectedItem['Filing Number']) {
          setLoading(true)
          api.GET_SERVICE_OF_PROCESS_FILING_DETAILS(
            selectedItem['Filing Number'],
            (filing) => {
              setLoading(false)
              dispatch({ type: SET_RECORD_DETAILS, record: filing })
            },
            (err) => {
              setLoading(false)
              console.error(err)
            }
          )
        }
        break
      case 'AthleteAgentSearch':
        if (selectedItem['SourceID']) {
          setLoading(true)
          api.GET_ATHLETE_AGENT_RECORD_DETAILS(
            selectedItem['SourceID'],
            (record) => {
              setLoading(false)
              dispatch({ type: SET_RECORD_DETAILS, record: record })
            },
            (err) => {
              setLoading(false)
              console.error(err)
            }
          )
        }
        break
      default:
    }
  }

  useEffect(() => {
    if (selectedItem) {
      getRecordDetails(searchFields.Name)
    }
  }, [searchFields, selectedItem])

  useEffect(() => {
    if (Object.keys(recordDetails).length > 0) setLoading(false)
  }, [recordDetails])

  const isInEmailFormat = (value) => {
    const emailRegex = new RegExp(/^[A-Za-z0-9_!#$%&'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/, 'gm')
    return emailRegex.test(value)
  }

  const formattedEmail = (value) => {
    const parts = value.split('@')
    return `${parts[0]}\n@${parts[1]}`
  }

  const specificDetail = useCallback(
    (col) => {
      const outputValue = selectedItem[col.origHeaderName ? col.origHeaderName : col.headerName]
      switch (col.headerName) {
        case 'Receipt #':
          return {
            label: col.headerName,
            value: appUserRights.includes('ReceiptView') ? (
              <span
                className="dln-link"
                onClick={() => {
                  history.push(`/accounting/receipt/${selectedItem[col.headerName]}`)
                }}
              >
                {selectedItem[col.headerName]}
              </span>
            ) : (
              selectedItem[col.headerName]
            ),
          }
        case 'DLN':
          const dln = selectedItem[col.headerName]
          return dln
            ? {
                label: col.headerName,
                value: (
                  <div
                    className={dln.indexOf(',') === -1 ? 'dlnTableLink' : ''}
                    tabIndex={dln.indexOf(',') === -1 ? 0 : -1}
                    onClick={() => {
                      return dln.indexOf(',') === -1
                        ? checkDLNValid(
                            dln,
                            (id) => {
                              dispatch(openSpecificImage(id, dln))
                            },
                            (dln) => {
                              dispatch(setErrorToast('DLN ' + dln + ' not found'))
                            }
                          )
                        : null
                    }}
                  >
                    {dln}
                  </div>
                ),
              }
            : { label: col.headerName, value: '' }
        case 'Email':
        case 'Username':
          return {
            label: col.headerName,
            value: isInEmailFormat(outputValue) ? formattedEmail(outputValue) : outputValue,
          }
        case 'IsStaff':
          return {
            label: col.headerName,
            value: outputValue ? 'Yes' : 'No',
          }
        case 'Prior Name(s)':
          return {
            label: col.headerName,
            value: outputValue.replaceAll('[linebreak]', '\n\n'),
          }
        case 'Status':
          if (typeof outputValue === 'object') {
            return {
              label: (
                <span
                  className={`red-flag-status-label ${outputValue.RedFlagStatus ? 'alert' : ''}`}
                >
                  {col.headerName}
                </span>
              ),
              value: (
                <span
                  className={`red-flag-status-cell ${outputValue.RedFlagStatus ? 'alert' : ''}`}
                >
                  {outputValue.Description}
                </span>
              ),
            }
          }
          return {
            label: col.headerName,
            value: outputValue,
          }
        default:
          return {
            label: col.headerName,
            value: outputValue,
          }
      }
    },
    [selectedItem]
  )

  const selectedRowDetails = useMemo(() => {
    if (!searchResults[searchFields.Title]) {
      return []
    }
    if (!selectedItem) {
      return []
    }

    if (selectedItem.id !== undefined) {
      switch (searchFields.Name) {
        // case 'BusinessEntitySearch': {
        //   const businessdetails = searchResults[searchFields.Title][selectedItem.id]
        //   return [
        //     { label: 'Name', value: businessdetails['Name'] },
        //     { label: 'Filing Number', value: businessdetails['Filing Number'] },
        //     { label: 'Record Type', value: businessdetails['Record Type'] },
        //     {
        //       label: 'Record Subtype',
        //       value: businessdetails['Record Sub-Type'],
        //       // recordDetails['FILING_SUBTYPE'] && recordDetails['FILING_SUBTYPE'].DESCR
        //       //   ? recordDetails['FILING_SUBTYPE'].DESCR
        //       //   : recordDetails['FILING_SUBTYPE'],
        //     },
        //     {
        //       label: 'Filing Date',
        //       value: businessdetails['Registration Date']
        //         ? format(new Date(businessdetails['Registration Date']), 'MM/dd/yyyy')
        //         : '',
        //     },
        //     { label: 'Status', value: businessdetails['Status'] },
        //   ]
        // }
        case 'TrademarkSearch': {
          const details = searchResults[searchFields.Title][selectedItem.id]
          if (!recordDetails.TrademarkClasses) {
            return []
          }
          if (details.SourceID != recordDetails.ID) {
            return []
          }
          return [
            { label: 'Trademark Type', value: details['Trademark Type'] || details['Record Type'] },
            {
              label: details['Trademark #'] ? 'Trademark #' : 'Record Number',
              value: details['Trademark #'] || details['Record Number'],
            },
            { label: 'Trademark Description', value: details['Mark Description'] },
            {
              label: 'Trademark Class #',
              value:
                recordDetails.TrademarkClasses[0] == null
                  ? ''
                  : recordDetails.TrademarkClasses[0].classNumber,
            },
            {
              label: 'Trademark Class',
              value:
                recordDetails.TrademarkClasses[0] == null
                  ? ''
                  : recordDetails.TrademarkClasses[0].shortName,
            },
            { label: 'Applicant Name', value: details['Owner Name'] },
            { label: 'Applicant City', value: details['Owner City'] },
            { label: 'Filing Date', value: details['Filing Date'] },
            { label: 'Status', value: details['Status'] },
            { label: 'Expiration Date', value: details['Expiration Date'] },
            { label: 'Entry Type', value: details['Entry Type'] },
          ]
        }
        case 'OrderSearch': {
          return columns
            .filter((col) => col.headerName !== 'View')
            .map((col) => {
              if (col.headerName === 'Status')
                return {
                  label: 'Status',
                  value: recordDetails.Status ? recordDetails.Status : selectedItem['Status'],
                }
              return specificDetail(col)
            })
        }
        case 'AthleteAgentSearch': {
          const aaDetails = []
          aaDetails.push({ label: 'Name', value: recordDetails.FullName })
          aaDetails.push({ label: 'Status', value: recordDetails.Status })
          aaDetails.push({ label: 'Record Type', value: recordDetails.RecordType })
          aaDetails.push({ label: 'Agency Name', value: recordDetails.AgencyName })
          aaDetails.push({ label: 'License #', value: recordDetails.LicenseNum })
          if (recordDetails.ForeignLicenseNum)
            aaDetails.push({ label: 'Foreign License #', value: recordDetails.ForeignLicenseNum }) //
          aaDetails.push({ label: 'Business Address', value: recordDetails.BusinessAddress })
          if (recordDetails.Email) aaDetails.push({ label: 'Email', value: recordDetails.Email }) //
          if (recordDetails.PhoneNumber)
            aaDetails.push({ label: 'Phone', value: recordDetails.PhoneNumber }) //
          aaDetails.push({ label: 'Registration Date', value: recordDetails.RegistrationDate })
          aaDetails.push({ label: 'Expiration Date', value: recordDetails.ExpirationDate })
          if (recordDetails.InactiveDate)
            aaDetails.push({ label: 'Inactive Date', value: recordDetails.InactiveDate }) //
          if (recordDetails.RenewalDate)
            aaDetails.push({ label: 'Renewal Date', value: recordDetails.RenewalDate }) //

          return aaDetails
        }
      }
    }
    //Default return
    return columns
      .filter((col) => col.headerName !== 'View')
      .filter((col) => col.headerName !== 'Old Name(s)' || selectedItem[col.headerName].length > 0)
      .map((col) => {
        return specificDetail(col)
      })
  }, [
    selectedItem,
    searchResults,
    recordDetails,
    searchFields.title,
    searchFields.Name,
    columns,
    specificDetail,
  ])

  const ViewRecordButton = useMemo(() => {
    if (searchFields.Name === 'TrademarkSearch' && !appUserRights.includes('TrademarkView')) {
      return null
    } else if (
      searchFields.Name === 'UserSearch' ||
      searchFields.Name === 'FacsimileSignatureSearch' ||
      searchFields.Name === 'OrderSearch' ||
      searchFields.Name === 'AthleteAgentSearch' ||
      searchFields.Name === 'ServiceOfProcessSearch' ||
      searchFields.Name === 'DLNSearch'
    ) {
      return null
    } else
      return (
        <ActionButton
          label="View Record"
          onClick={() => {
            history.push(getRedirect(selectedItem))
          }}
          children={<IconSearch />}
        />
      )
  }, [searchFields, selectedItem])

  const EditRecordButton = useMemo(() => {
    if ((recordDetails.EditRedirectUrl || '') === '') {
      return null
    }
    return (
      <ActionButton
        label="Edit Record"
        onClick={() => {
          history.push({pathname: recordDetails.EditRedirectUrl})
        }}
        children={<IconPencil />}
      />
    ) 

  }, [recordDetails])

  const voidInfo = useMemo(() => {
    switch (searchFields.Name) {
      case 'FacsimileSignatureSearch':
      case 'OrderSearch':
        return recordDetails.VoidInformation
      default:
        return null
    }
  }, [recordDetails, searchFields])

  const voidCategory = useMemo(() => {
    switch (searchFields.Name) {
      case 'FacsimileSignatureSearch':
        return 'FacsimileSignature'
      case 'OrderSearch':
        return 'Orders'
      default:
        return ''
    }
  }, [searchFields])

  const voidFilingNum = useMemo(() => {
    switch (searchFields.Name) {
      case 'FacsimileSignatureSearch':
        return recordDetails.FacsimileSignatureNum
      case 'OrderSearch':
        return selectedItem ? selectedItem['Certification Filing #'] : ''
      default:
        return ''
    }
  }, [recordDetails, selectedItem])

  const voidLabel = useMemo(() => {
    switch (searchFields.Name) {
      case 'FacsimileSignatureSearch':
        return 'Facsimile Signature'
      case 'OrderSearch':
        return 'Order'
      default:
        return ''
    }
  }, [searchFields])

  const showRecordHistory = useMemo(() => {
    switch (searchFields.Name) {
      case 'AthleteAgentSearch':
        return true
      default:
        return false
    }
  }, [searchFields])

  if (isMiscSearch) {
    return (
      <div>
        <DetailList
          details={miscSearchTemplate.map((col) => ({
            label: col.Header,
            value: selectedItem[col.FieldName] ?? '',
          }))}
          topBorder
          bottomBorder
          labelWidth={40}
          customCss={{ '> div': { '> div': { fontSize: '.8em' } } }}
        />
        <div className={'view-action'}>
          <ActionButton
            label="View Record"
            onClick={() => {
              history.push(`/recordDetails/${sourceTypeParam}/${selectedItem.RecordId}?moduleId=${moduleId}`)
            }}
            children={<IconSearch />}
          />
        </div>
      </div>
    )
  }

  if (selectedRowDetails.length <= 0 || loading) {
    return (
      <div className="loader-wrapper bo">
        <Spinner className="page-loader" themeColor="dark-neutral" />
      </div>
    )
  }

  return (
    <div>
      <DetailList
        details={selectedRowDetails}
        topBorder
        bottomBorder
        labelWidth={40}
        customCss={{ '> div': { '> div': { fontSize: '.8em' } } }}
      />
      {searchFields.Voidable === true && <VoidInfo voidInfo={voidInfo} loading={loading} />}

      {(searchFields.Name === 'FacsimileSignatureSearch' ||
        searchFields.Name === 'OrderSearch' ||
        searchFields.Name === 'ServiceOfProcessSearch') && (
        <div className="staff-search-downloads">
          <CorrespondenceDownload
            template={{ label: 'Available Downloads:' }}
            value={
              recordDetails.CorrespondenceDownloads ? recordDetails.CorrespondenceDownloads : ''
            }
          />
        </div>
      )}
      <div className={'view-action'}>
        {searchFields.Name === 'ReceiptSearch' &&
          (selectedItem['Receipt Status'] === 'Posted' ||
            selectedItem['Receipt Status'] === 'Balanced') && (
            <ActionButton
              label="Print Receipt"
              onClick={() => {
                dispatch({
                  type: ADD_PDF_TO_LIST,
                  pdfInfo: {
                    filename: `Receipt-${selectedItem['Receipt #']}.pdf`,
                    type: pdfTypes.RECEIPT,
                    ReceiptNum: selectedItem['Receipt #'],
                  },
                })
              }}
              children={<PrintIcon />}
            />
          )}
        {searchFields.Name === 'DLNSearch' && selectedItem.SourceID && (
          <React.Fragment>
            <ActionButton
              label="View Envelope Reports"
              onClick={() => {
                dispatch({
                  type: ADD_PDF_TO_LIST,
                  pdfInfo: {
                    filename: `Envelope-${selectedItem.SourceID}.pdf`,
                    type: pdfTypes.IMAGE_ENVELOPE,
                    imageEnvelopeId: selectedItem.SourceID,
                  },
                })
              }}
              children={<PrintIcon />}
            />
            <ActionButton
              label="View Image"
              onClick={() => {
                dispatch(openSpecificImage(selectedItem.SourceID, selectedItem.DLN))
              }}
              children={<IconSearch />}
            />
          </React.Fragment>
        )}
        {ViewRecordButton}
        {EditRecordButton}
        {((searchFields.Name === 'OrderSearch' && appUserRights.includes('OrdersVoid')) ||
          (searchFields.Name === 'FacsimileSignatureSearch' &&
            appUserRights.includes('FacsimileVoid'))) &&
          searchFields.Voidable === true &&
          !voidInfo &&
          !loading && (
            <React.Fragment>
              <ActionButton
                label="Void"
                onClick={() => {
                  setVoidModalOpen(true)
                }}
                children={<CrossHeavy />}
              />
            </React.Fragment>
          )}
        {showRecordHistory && !loading && (
          <React.Fragment>
            <ActionButton
              label={VIEW_HISTORY || 'View History'}
              onClick={() => {
                setHistoryModalOpen(true)
              }}
              children={<IconSearch />}
            />
          </React.Fragment>
        )}
      </div>

      {searchFields.Voidable === true && (
        <VoidModal
          label={voidLabel}
          voidModalOpen={voidModalOpen}
          setVoidModalOpen={setVoidModalOpen}
          voidMethod={(info) =>
            VOID_FILING(
              voidFilingNum,
              voidCategory,
              info,
              () => {
                getRecordDetails(searchFields.Name)
              },
              (error) => {
                dispatch(setErrorToast('Failed to void filing.'))
              }
            )
          }
        />
      )}

      {showRecordHistory && (
        <SearchRecordHistory
          historyModalOpen={historyModalOpen}
          setHistoryModalOpen={setHistoryModalOpen}
          searchFields={searchFields}
        />
      )}
    </div>
  )
}

export default SearchItemInfo

const VoidInfo = ({ voidInfo, loading }) => {
  const details = useMemo(() => {
    const returnVal = []
    if (voidInfo) {
      if (voidInfo.VoidedBy) returnVal.push({ label: 'Voided By', value: voidInfo.VoidedBy })
      if (voidInfo.VoidDate)
        returnVal.push({ label: 'Date', value: moment(voidInfo.VoidDate).format('MM/DD/YYYY') })
      if (voidInfo.Reason) returnVal.push({ label: 'Reason', value: voidInfo.Reason })
    }

    return returnVal
  }, [voidInfo])

  if (loading) return null

  return details.length > 0 ? (
    <div>
      <DetailList
        label={`Void Information`}
        details={details}
        topBorder
        bottomBorder
        customCss={{ '> div': { '> div': { fontSize: '.8em' } } }}
      />
    </div>
  ) : null
}
